import LiveTooltip from 'live/components/ui/tooltip'
import { DashboardContext } from 'live/context/dashboard'
import useSetParamsToRouter from 'live/lib/hooks/useSetParamsToRouter'
import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'
import { isEqual } from 'lodash'
import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { ParameterProps } from '../types'
import { RUN_PARAMETERS_KEY } from './ParametersButton'
import { StyledParametersList } from './style'
import { getHashParametersFiltered } from './utils'

function canClickApplyButton() {
    return !!(authorization.Permission.isAdmin || authorization.Permission.TRY_RUN_PARAMETERS || authorization.Permission.MANAGE_RUN_PARAMETERS)
}

function canClickSaveButton() {
    return !!(authorization.Permission.isAdmin || authorization.Permission.MANAGE_RUN_PARAMETERS)
}

interface ParametersListProps {
    dark: any
    displayParameters: ParameterProps[]
    onResetParameters: ParameterProps[]
    updateByUser: Function
    updateByRun: Function
    pluginHeaderNavigationDetected: boolean
    contextParams: any
    span: any
    setDiffParametersFlag: Function
    history: Map<string, any>[]
    setUpdateHistory: Function
    updateHistory: boolean
    defaultRunparameters: ParameterProps[]
    deleteRunParametersHistoryItem: Function
}

const deleteHistoryItem = (
    createdAtTimestamp
) => {
    return fetch('/services/plugin-annotations/annotations/' + annotationId, {
        method: 'DELETE'
    }).then(r => {
        if (!r.ok) {
            new Error(r.statusText)
        } else {
            setSelectedAnnotationId(null)
            setUpdateHistory(!updateHistory)
        }
    })
}

const ParametersList = function ({
    dark,
    displayParameters,
    onResetParameters,
    defaultRunparameters,
    updateByUser,
    updateByRun,
    pluginHeaderNavigationDetected,
    contextParams,
    span,
    setDiffParametersFlag,
    history,
    setUpdateHistory,
    updateHistory,
    deleteRunParametersHistoryItem
}: ParametersListProps) {
    const [newParameters, setNewParameters] = useState<ParameterProps[]>(displayParameters.map(e => ({ ...e })))
    const { state, params, ...context } = useContext(DashboardContext)
    const setParamsToRouter = useSetParamsToRouter(true)
    const [showHistory, setShowHistory] = useState(false)
    const [selectedAnnotationId, setSelectedAnnotationId] = useState(null)
    const unappliedChanges = !isEqual(displayParameters, newParameters)
    const unsavedChanges = !isEqual(onResetParameters, newParameters)
    const newParametersBeforeHistorySelectionRef = useRef(newParameters)

    function updateDisplayParameters(value: any, pos: number) {
        value = value == "" ? null : value
        const updatedParam = newParameters.map((e, i) => ({ ...e, value: (i === pos ? value : e.value) }))
        setNewParameters(updatedParam)
        setSelectedAnnotationId(null)
    }

    function updateNewParametersFromSelectedHistory(historyKeyValueMap) {
        if (historyKeyValueMap === null) {
            setNewParameters(newParametersBeforeHistorySelectionRef.current)
        } else {
            if (selectedAnnotationId === null)
                newParametersBeforeHistorySelectionRef.current = newParameters

            const newParametersUpdatedFromHistorySelected = newParameters.map(p => ({ ...p, value: historyKeyValueMap[p.key] ?? p.value }))
            setNewParameters(newParametersUpdatedFromHistorySelected)
        }
    }

    function onResetToApplied() {
        setNewParameters(displayParameters)
    }

    function onResetToSaved() {
        setNewParameters(onResetParameters)
    }

    function onSave() {
        updateParametersOnHash(newParameters)
        // NOTE:
        // updateByUser with updateDashboardFlag:false to make sure the dashboard is updated/reloaded only once
        // This is not ideal, but it works for now. Ideally the parent should control the reload of the dashboard
        // to avoid muitple updates/reloads.
        updateByUser(newParameters, false)
        updateByRun(newParameters)
    }

    function onApply() {
        updateParametersOnHash(newParameters)
        updateByUser(newParameters)
    }

    const updateParametersOnHash = parameters => {
        let hashValues = getHashParametersFiltered(window.location.hash, contextParams)

        setParamsToRouter({
            ...contextParams,
            ...hashValues,
            [RUN_PARAMETERS_KEY]: encodeURIComponent(
                encodeURIComponent(JSON.stringify(parameters.map(({ priority, ...attrs }) => attrs)))
            )
        })
    }

    useEffect(() => setDiffParametersFlag(unappliedChanges || unsavedChanges),
        [JSON.stringify(unappliedChanges), JSON.stringify(unsavedChanges)])

    return (
        <StyledParametersList
            dark={dark}
            showHistory={showHistory}
            pluginHeaderNavigationDetected={pluginHeaderNavigationDetected}
            hasFooter={canClickApplyButton() || canClickSaveButton()}
        >
            <div className="parameters-main">
                <div className='parameters-left-pane'>
                    {/*<div className='paramters-header'>
                        <a className='toggle-history-button' onClick={() => setShowHistory(!showHistory)}>{showHistory ? 'Hide history' : 'View history'}</a>
                    </div>*/}
                    <div className="parameters-list">
                        <ul>
                            {newParameters.map(({ name, value, uom }, i) => {
                                return (
                                    <li key={i}>
                                        <div>
                                            <label>{name}{!!uom ? ` (${uom})` : ""}</label>
                                            <div style={{ display: 'inline-flex', width: '100%' }}>
                                                <input
                                                    type="text"
                                                    value={value}
                                                    onChange={e => updateDisplayParameters(e.target.value, i)}
                                                    disabled={!canClickApplyButton() && !canClickSaveButton()}
                                                />
                                            </div>
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                    {canClickApplyButton() ? <MainActions dark={dark}>
                        <div className="buttons">
                            <div className="left">
                                <ActionHeader>
                                    <span>Dashboard Actions</span>
                                    <LiveTooltip position="top" content={i18n('Parameters are stored by user and run, applying to the dashboard context only.')}>
                                        <i className="far fa-info-circle" />
                                    </LiveTooltip>
                                </ActionHeader>
                                <WarnText>{canClickApplyButton() && unappliedChanges ? i18n('* Unapplied changes') : ''}</WarnText>
                                <div className="flex">
                                    <button className="btn btn-primary" onClick={onApply} disabled={!canClickApplyButton() || (canClickApplyButton() && !unappliedChanges)} >Apply</button>
                                    <ResetButton onClick={onResetToApplied} disabled={!canClickApplyButton() || (canClickApplyButton() && !unappliedChanges)}>Reset</ResetButton>
                                </div>
                            </div>
                            <Separator />
                            <div>
                                <ActionHeader>
                                    <span>Run Actions</span>
                                    <LiveTooltip position="top" content={i18n('Parameters are stored by run and apply on all contexts (e.g. dashboards, alarms, aggregations, etc).')}>
                                        <i className="far fa-info-circle" />
                                    </LiveTooltip>
                                </ActionHeader>
                                <WarnText>{canClickSaveButton() && unsavedChanges ? i18n('* Unsaved changes') : ''}</WarnText>
                                <div className="flex">
                                    <button className="btn btn-primary" onClick={onSave} disabled={!canClickSaveButton() || (canClickSaveButton() && !unsavedChanges)}>Save</button>
                                    <ResetButton onClick={onResetToSaved} disabled={!canClickSaveButton() || (canClickSaveButton() && !unsavedChanges)}>Reset</ResetButton>
                                </div>
                            </div>
                        </div>
                    </MainActions> : null}
                </div>
                {/*<div className='parameters-right-pane'>
                    {showHistory && (<div className='paramters-header'>
                        <h3>History</h3>
                    </div>)}
                    {showHistory && (
                        <div className="parameters-history">
                            <HistoryTable
                                defaultRunparameters={defaultRunparameters}
                                history={history}
                                updateNewParametersFromSelectedHistory={updateNewParametersFromSelectedHistory}
                                selectedAnnotationId={selectedAnnotationId}
                                setSelectedAnnotationId={setSelectedAnnotationId}
                                deleteRunParametersHistoryItem={deleteRunParametersHistoryItem}
                            />
                        </div>
                    )}
                </div>*/}
            </div>
        </StyledParametersList>
    )
}

const ActionHeader = styled.div`
    font-family: var(--font-semibold) !important;
`

const Separator = styled.div`
    border-left: solid 1px lightgray;
    padding-right: 14px;
`

const WarnText = styled.div`
    height: 16px;
    color: var(--brand-warning);
    line-height: 12px;
    font-size: 10px;
`

const ResetButton = styled.button`
    background: none !important;
    border: none;
    padding: 0px !important;
    color: ${props => props.disabled ? 'var(--gray-light) !important' : 'var(--color-link) !important'};
    text-decoration: ${props => props.disabled ? '' : 'underline'};
    cursor: ${props => props.disabled ? '' : 'pointer !important'};
    margin-left: 10px !important;
`

const MainActions = styled.div`
    border-top: 1px solid var(--gray-300);
    padding: 15px 20px 0px;
    flex-flow: column;
    display: flex;
    width: 100%;

    i {
        margin-left: 5px;
    }

    .btn-primary {
        height: auto !important;
        margin: 0px !important;
    }

    .buttons {
        display: flex;
        justify-content: space-between;

        .left {
            width: 100%;
            margin: 0px !important;
            text-align: left;
            white-space: nowrap;
        }

        .right {
            display: flex;
        }
    }
`

export default ParametersList
// export default onClickOutside(ParametersList, {
//     handleClickOutside: () => ParametersList.toggle
// })
